import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { PricingEventAttribution, ProximityDisplay } from "~/types/search";

// An applied promo code on the hold. `total_amount` on the context stays the
// original list price; the discounted amount the driver pays is
// `driver_payable_amount` here. payment_requirement === "no_payment_required"
// means a 100%-off coupon (confirm without a PaymentIntent).
export interface AppliedCoupon {
  code: string;
  coupon_name: string;
  discount_amount: number;
  driver_payable_amount: number;
  payment_requirement: "payment_required" | "no_payment_required";
}

export interface BookingContext {
  listing_id: string;
  starts_at: string;
  ends_at: string;
  vehicle_type: string;
  vehicle_types: string[];
  spot_name: string;
  spot_address: string;
  spot_type: string;
  /**
   * The spot's IANA zone, carried from the hold so checkout renders the booking
   * window on the spot's clock rather than the driver's device. Absent against a
   * backend that predates the field; formatters fall back to viewer-local.
   */
  timezone?: string | null;
  base_amount: number;
  fee_amount: number;
  total_amount: number;
  dest_lat: number;
  dest_lng: number;
  dest_address?: string;
  dest_name?: string;
  hold_id: string;
  expires_at: string;
  searchUrl: string;
  distance_miles?: number;
  walking_minutes?: number;
  proximity_display?: ProximityDisplay;
  // Amenities for the checkout summary. `ev_charger` comes from search; `gated`
  // and `security_camera` come from the hold's `spot_details` (not in search).
  ev_charger?: boolean;
  gated?: boolean;
  security_camera?: boolean;
  vehicle_id?: string;
  coupon?: AppliedCoupon | null;
  /**
   * The event that priced this hold, if any — taken from the hold's own frozen
   * breakdown rather than the search result, so checkout names the event and
   * the quote it's explaining come from the same place. Absent on ordinary
   * pricing and on holds created before the backend shipped the field.
   */
  event?: PricingEventAttribution | null;
  /**
   * Set at a confirm's first server write for this hold (the PaymentIntent, or
   * the $0 confirm) and never cleared; a new hold replaces the whole context,
   * which resets it. Once set, release callers skip the release: the backend
   * may already have converted the hold (a lost response looks like a
   * failure), and an unreleased hold just expires.
   */
  confirm_started?: boolean;
}

interface BookingStore {
  context: BookingContext | null;
  setContext: (ctx: BookingContext) => void;
  setVehicleId: (vehicleId: string) => void;
  setCoupon: (coupon: AppliedCoupon | null) => void;
  /** Marks the current hold's confirm as sent. See `confirm_started`. */
  markConfirmStarted: () => void;
  clear: () => void;
}

export const useBookingStore = create<BookingStore>()(
  persist(
    (set) => ({
      context: null,
      setContext: (context) => set({ context }),
      setVehicleId: (vehicle_id) =>
        set((s) => ({
          context: s.context ? { ...s.context, vehicle_id } : null,
        })),
      setCoupon: (coupon) =>
        set((s) => ({
          context: s.context ? { ...s.context, coupon } : null,
        })),
      markConfirmStarted: () =>
        set((s) => ({
          context: s.context ? { ...s.context, confirm_started: true } : null,
        })),
      clear: () => set({ context: null }),
    }),
    {
      name: "ezspot_booking_hold",
      storage: createJSONStorage(() => sessionStorage),
      onRehydrateStorage: () => (state) => {
        if (
          state?.context &&
          new Date(state.context.expires_at).getTime() <= Date.now()
        ) {
          state.clear();
        }
      },
    },
  ),
);
